import { useCallback, useMemo, useState } from 'react'
import { IAimProfilePatch, IAimStatus, liveAimStatus } from '@common/interfaces/user'
import { useAimStatusTick } from '../../hooks/useAimStatusTick'

/**
 * The viewer's own manual status plus the open/save wiring for AimStatusModal, shared by
 * the desktop and mobile headers. `ownAimStatus` is un-expired (memoized on the user
 * object), so expiry is applied here against a ticking instant — see useAimStatusTick.
 */
export const useOwnAimStatus = (
  ownAimStatus: IAimStatus | null,
  updateAimProfile: (patch: IAimProfilePatch) => unknown,
) => {
  const now = useAimStatusTick()
  const liveStatus = useMemo(() => liveAimStatus(ownAimStatus, now), [ownAimStatus, now])

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)

  const handleOpenStatusModal = useCallback(() => setIsStatusModalOpen(true), [])

  const handleCloseStatusModal = useCallback(() => setIsStatusModalOpen(false), [])

  const handleSaveStatus = useCallback(
    (patch: IAimProfilePatch) =>
      updateAimProfile(patch) as Promise<{ error?: unknown } | undefined>,
    [updateAimProfile],
  )

  return {
    liveStatus,
    isStatusModalOpen,
    handleOpenStatusModal,
    handleCloseStatusModal,
    handleSaveStatus,
  }
}
